import { useApolloClient } from '@apollo/client'
import { Observable } from '@apollo/client/utilities'

import {
  HeadcountRequestsDocument,
  PlanningValueObjectsRequestStatusEnum,
} from 'generated/resources/graphql'

import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'
import useHeadcountActualEmployees from 'modules/planning/hooks/headcount/useHeadcountActualEmployees'
import {
  mapSalaryFilterToVariables,
  mapWorkingTimeFilterToVariables,
  type NumberFilterValue,
} from 'modules/planning/lib/mapWorkingTimeFilterToVariables'
import { mapRequestToHeadcountRow } from 'modules/planning/hooks/headcount/mappers/headcountRequestMapper'
import { type HeadcountRow, type HeadcountChildrenProps } from 'modules/planning/types/headcount'
import { PARENT_NEW_HEADCOUNT } from 'modules/planning/consts/headcount'
import { useExtractJobRoleFilterValues } from 'modules/job_catalog/lib/filters/jobRole/values'
import useHasJobCatalogTree from './useHasJobCatalogTree'
import useHasContractsDataAsJson from './useHasContractsDataAsJson'

const PAGE_SIZE = 20

interface UseHeadcountChildrenParams {
  periodId: string
  planningId?: string
  periodStartDate?: string
  periodEndDate?: string
}

type PaginationParam = {
  total: number
  perPage: number
  currentPage: number
  pagesCount: number
  hasMore: boolean
}

export type HeadcountFilters = {
  managerId?: string[]
  locationIds?: string[]
  directManagerIds?: string[]
  jobRole?: string[]
  jobRoleLevel?: string[]
  statuses?: PlanningValueObjectsRequestStatusEnum[]
  workingTime?: NumberFilterValue
  salary?: NumberFilterValue
}

const useHeadcountChildren = ({
  periodId,
  planningId,
  periodStartDate,
  periodEndDate,
}: UseHeadcountChildrenParams) => {
  const client = useApolloClient()
  const isVersionDataEnabled = useHasContractsDataAsJson()
  const { fetchActualEmployees } = useHeadcountActualEmployees({
    planningPeriodId: periodId,
    periodStartDate,
    periodEndDate,
    isVersionDataEnabled,
  })
  const hasJobCatalogTreeFeature = useHasJobCatalogTree()
  const extractJobRoleFilterValues = useExtractJobRoleFilterValues({ hasJobCatalogTreeFeature })

  useOneMutationSubscription('BulkCreatePlanningRequests', HeadcountRequestsDocument, true)

  const fetchChildren = ({
    item,
    search,
    filters,
    pagination,
  }: {
    search?: string
    item: HeadcountRow
    filters?: HeadcountFilters
    pagination?: PaginationParam
  }) => {
    const endCursor =
      pagination && 'endCursor' in pagination
        ? (pagination satisfies Record<string, unknown>)['endCursor']
        : null
    const currentPageValue = pagination?.currentPage ?? 0
    const managerId =
      filters && 'managerId' in filters && filters.managerId ? String(filters.managerId) : undefined
    const locationIds = filters?.locationIds
    const directManagerIds = filters?.directManagerIds
    const { jobCatalogTreeNodeIds, jobLevelIds } = extractJobRoleFilterValues(filters)
    const jobCatalogLevelIds = !hasJobCatalogTreeFeature ? jobLevelIds : undefined
    const jobCatalogTreeLevelUuids = hasJobCatalogTreeFeature ? jobCatalogTreeNodeIds : undefined
    const statuses = filters?.statuses
    const workingTimeVariables = filters?.workingTime
      ? mapWorkingTimeFilterToVariables(filters.workingTime)
      : {}
    const salaryVariables = filters?.salary ? mapSalaryFilterToVariables(filters.salary) : {}
    if (item.parentType === PARENT_NEW_HEADCOUNT) {
      const after = typeof endCursor === 'string' ? endCursor : null
      const currentPage = currentPageValue
      const headcountManagerIds = managerId ? [managerId] : undefined
      const requestFilters = {
        search,
        locationIds,
        directManagerIds,
        jobCatalogLevelIds,
        jobCatalogTreeLevelUuids,
        statuses,
        workingTimeGteq: workingTimeVariables.workingTimeGteq,
        workingTimeLteq: workingTimeVariables.workingTimeLteq,
        salaryGteq: salaryVariables.salaryGteq,
        salaryLteq: salaryVariables.salaryLteq,
      }
      const hasRequestFilters = Object.values(requestFilters).some((value) => value !== undefined)

      return client
        .watchQuery({
          query: HeadcountRequestsDocument,
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: false,
          variables: {
            planningPeriodId: periodId,
            requestTypes: ['headcount'] as const,
            headcountManagerIds,
            filters: hasRequestFilters ? requestFilters : undefined,
            first: PAGE_SIZE,
            after,
          },
        })
        .map(({ data, loading, error }) => {
          const connection = data?.planning.requestsConnection
          const baseIndex = currentPage * PAGE_SIZE
          const records = (connection?.edges ?? []).map((edge, i) =>
            mapRequestToHeadcountRow(edge, baseIndex + i)
          )
          const totalCount = connection?.totalCount ?? 0
          const pageInfo = connection?.pageInfo
          const page = currentPage + 1

          return {
            loading,
            error,
            data: {
              records,
              type: 'detailed' as const,
              paginationInfo: {
                total: totalCount,
                perPage: PAGE_SIZE,
                currentPage: page,
                pagesCount: Math.ceil(totalCount / PAGE_SIZE),
                hasMore: pageInfo?.hasNextPage ?? false,
                endCursor: pageInfo?.endCursor ?? null,
              },
            },
          }
        })
    }

    return new Observable<HeadcountChildrenProps>((subscriber) => {
      const fetchData = async () => {
        const endCursorStr = typeof endCursor === 'string' ? endCursor : null
        const rootEmployeeId = item.rowKind === 'parent' ? item.rootEmployeeId : undefined
        const result = await fetchActualEmployees(
          {
            ...pagination,
            total: pagination?.total ?? 0,
            perPage: pagination?.perPage ?? PAGE_SIZE,
            currentPage: pagination?.currentPage ?? 0,
            pagesCount: pagination?.pagesCount ?? 0,
            hasMore: pagination?.hasMore ?? false,
            endCursor: endCursorStr,
          },
          rootEmployeeId,
          locationIds,
          directManagerIds,
          jobCatalogLevelIds,
          jobCatalogTreeLevelUuids,
          search
        )
        subscriber.next({
          loading: false,
          error: undefined,
          data: {
            records: result.records,
            type: 'detailed',
            paginationInfo: result.paginationInfo,
          },
        })
      }
      fetchData()
    })
  }

  return { fetchChildren }
}

export default useHeadcountChildren
